import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, X, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const RewardsTab = ({ api, activeKid }) => {
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', cost: 50, emoji: '🎁', description: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [r, red] = await Promise.all([api.get('/kids/rewards'), api.get('/kids/redemptions')]);
      setRewards(r.data); setRedemptions(red.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveReward = async (payload) => {
    try { await api.post('/kids/rewards', payload); toast.success('Reward added'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await saveReward({ ...form, cost: parseInt(form.cost) });
    setShowForm(false);
    setForm({ title: '', cost: 50, emoji: '🎁', description: '' });
  };

  const getAISuggestions = async () => {
    setLoadingAI(true);
    try {
      const r = await api.post('/kids/ai-suggest', { kid_id: activeKid?.kid_id || '', kind: 'rewards' });
      setSuggestions(r.data.suggestions || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'AI suggestion failed'); }
    setLoadingAI(false);
  };

  return (
    <div className="space-y-5" data-testid="kids-rewards-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Rewards store — kids redeem their ⭐ points here, you approve requests in the Approvals tab</p>
        <div className="flex gap-2">
          <button onClick={getAISuggestions} disabled={loadingAI} data-testid="ai-suggest-rewards-btn"
            className="px-3 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
            <Sparkle size={14} /> {loadingAI ? 'Thinking…' : 'AI Suggest'}
          </button>
          <button onClick={() => setShowForm(true)} data-testid="add-reward-btn"
            className="px-3 py-2 rounded-full bg-[#FF6B35] text-white text-sm font-semibold flex items-center gap-1.5"><Plus size={14} /> Add Reward</button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="glass-card p-4 border-purple-500/30" data-testid="reward-suggestions-box">
          <p className="text-xs font-semibold text-purple-600 mb-2 flex items-center gap-1"><Sparkle size={12} /> AI reward ideas — tap to add</p>
          <div className="flex gap-2 flex-wrap">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => { saveReward({ title: s.title, cost: s.cost || 50, emoji: s.emoji || '🎁' }); setSuggestions(prev => prev.filter((_, j) => j !== i)); }}
                className="px-3 py-1.5 rounded-full border border-purple-500/40 bg-purple-500/5 text-sm hover:bg-purple-500/15">
                {s.emoji} {s.title} <span className="text-amber-600 font-semibold">{s.cost} pts</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {rewards.map(r => (
          <div key={r.reward_id} className="glass-card p-4 relative group" data-testid={`reward-${r.reward_id}`}>
            <button onClick={async () => { await api.delete(`/kids/rewards/${r.reward_id}`); fetchData(); }}
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive"><Trash size={13} /></button>
            <span className="text-3xl">{r.emoji}</span>
            <p className="text-sm font-semibold mt-2 leading-tight">{r.title}</p>
            {r.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{r.description}</p>}
            <p className="text-sm font-bold text-amber-600 mt-2">⭐ {r.cost}</p>
          </div>
        ))}
        {rewards.length === 0 && <p className="col-span-full text-center py-10 text-sm text-muted-foreground">No rewards yet — add some to motivate the kids!</p>}
      </div>

      {redemptions.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="font-semibold text-sm mb-3">Redemption History</h3>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {redemptions.map(r => (
              <div key={r.redemption_id} className="flex items-center justify-between text-sm">
                <span>{r.reward_emoji} {r.reward_title} <span className="text-xs text-muted-foreground">· {r.cost} pts</span></span>
                <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${r.status === 'approved' ? 'bg-green-500/10 text-green-600' : r.status === 'rejected' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-600'}`}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-card rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="reward-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold">Add Reward</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <input data-testid="reward-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                placeholder="e.g. 1 hour extra screen time" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground">Cost (points)</label>
                  <input data-testid="reward-cost-input" type="number" min="1" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Emoji</label>
                  <input value={form.emoji} onChange={e => setForm({ ...form, emoji: e.target.value })}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
                </div>
              </div>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Description (optional)" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              <button type="submit" data-testid="reward-save-btn" className="w-full px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">Add Reward</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
